import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { useNavigate } from 'react-router';
import { GoogleLogin } from '@react-oauth/google';
import { usePostHog } from '@posthog/react';
import { authActions } from '../../actions/auth';
import authSlice from '../../utils/redux/slice/authSlice';
import './AuthScreen.css';

function LoginScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const { email, password } = loginData;
    try {
      const data = await authActions.login(email, password);
      dispatch(authSlice.actions.setUser(data.user));
      dispatch(authSlice.actions.setToken(data.token));
      dispatch(authSlice.actions.setRefreshToken(data.refreshToken));
      dispatch(authSlice.actions.setPermissions(data.permissions));

      // PostHog: Identify user and capture login event
      posthog?.identify(data.user?.client_id || email, {
        email: email,
        name: data.user?.client_name,
      });
      posthog?.capture('user_logged_in', {
        method: 'email',
      });

      message.success('Login successful');
    } catch (error) {
      posthog?.capture('login_failed', {
        method: 'email',
        error_status: error?.response?.status,
      });
      if (error?.response?.status === 401) {
        message.error('Invalid email or password');
      } else if (error?.code === 'ECONNABORTED' || error?.code === 'ERR_NETWORK') {
        // A stall used to leave the button on "Signing in…" forever. Now it
        // ends, and says the one thing worth checking.
        message.error('Could not reach FluxUro. Check your connection and try again.');
      } else {
        message.error('Something went wrong, please try again');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const data = await authActions.googleAuth(credentialResponse.credential);
      dispatch(authSlice.actions.setUser(data.user));
      dispatch(authSlice.actions.setToken(data.token));
      dispatch(authSlice.actions.setRefreshToken(data.refreshToken));
      dispatch(authSlice.actions.setPermissions(data.permissions));

      // PostHog: Identify user and capture login event for Google OAuth
      posthog?.identify(data.user?.client_id || data.user?.client_email, {
        email: data.user?.client_email,
        name: data.user?.client_name,
      });
      posthog?.capture('user_logged_in', {
        method: 'google',
      });

      message.success('Login successful');
    } catch (error) {
      console.error('Google login error:', error);
      posthog?.capture('login_failed', {
        method: 'google',
        error_status: error?.response?.status,
      });
      const errorMsg = error?.response?.data?.error || 'Google sign-in failed. Please try again.';
      message.error(errorMsg);
    }
  };

  const handleGoogleError = () => {
    message.error('Google sign-in failed. Please try again.');
  };

  return (
    <div className="auth-container">
      {[...Array(6)].map((_, i) => (
        <span key={i} className="bubble" />
      ))}
      <div className="auth-card">
        <div className="logo-container">
          <img src="/uro_logo_white.png" alt="URO" className="logo" />
        </div>
        <h2>Login</h2>
        <form className="auth-form" onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="loginEmail">Email</label>
            <input
              id="loginEmail"
              type="email"
              value={loginData.email}
              onChange={(e) =>
                setLoginData({ ...loginData, email: e.target.value })
              }
              autoComplete="email"
              placeholder="Enter your email"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="loginPassword">Password</label>
            <input
              id="loginPassword"
              type="password"
              value={loginData.password}
              onChange={(e) =>
                setLoginData({ ...loginData, password: e.target.value })
              }
              autoComplete="current-password"
              placeholder="Enter your password"
              required
              minLength={6}
            />
          </div>
          <button
            className="button"
            type="submit"
            disabled={isSubmitting}
            style={{ opacity: isSubmitting ? 0.65 : 1, cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
          >
            {isSubmitting ? 'Signing in…' : 'Sign in'}
          </button>

          <div className="divider">
            <span>or</span>
          </div>

          <div className="google-button-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              shape="pill"
              text="continue_with"
            />
          </div>

          <button
            type="button"
            className="link-button"
            onClick={() => navigate('/register')}
          >
            Create an account
          </button>
          <button
            type="button"
            className="link-button"
            onClick={() => navigate('/forgot-password')}
          >
            Forgot password?
          </button>
        </form>
      </div>
    </div>
  );
}

export default LoginScreen;
